/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { createContext, useContext, useRef } from 'react'
import { authApi } from './auth'
import { useCompanyStore } from '../store/company/companyStore'
import { AuthFormData } from '../types/auth'
import { apiClient } from './apiClient'
import { PaginatedResponse } from './types'
import { useTaxSystem } from '../hooks/useTaxSystem'
import { initialCompany } from '../store/slices/companySlice'
import {
  Company,
  FFEDocument,
  LineItem,
  Material,
  ProductLibraryItem,
  Supplier,
  TeamMember,
  User
} from '../types/ffe'
import debounce from 'debounce'
// import { GoogleReCaptcha } from 'react-google-recaptcha-v3'
import { CompletedPurchaseOrder } from '../types/purchaseOrder'
import imageCompression from 'browser-image-compression'
import { generateNextCode } from '../utils/code'

export interface Organisation {
  _id: string
  name: string
  address: string
  country: string
  phone: string
  email: string
  logo: string
  primaryColor: string
  defaultMargin: number
  plan: 'free' | 'standard' | 'premium'
  isXeroConnected: boolean
  xeroNeedsReconnect?: boolean
  taxRate: number
  taxNumber: string
  purchaseOrderSettings?: {
    internalDescriptionVisible?: boolean
    defaultDeliveryNotes?: string
  }
  projectSettings?: {
    defaultHeader?: string
    defaultFooter?: string
  }
  createdAt?: string
}

type FindQueryParams<T> = {
  $limit?: number
  $skip?: number
  $search?: string
  $select?: Array<keyof T>
  $sort?: { [key in keyof T]?: 1 | -1 }
  query?: { [key in keyof T]?: any }
}

export type XeroConnection = {
  _id: string
  scopes: Array<string>
  xeroExpensesAccountId?: string
  createdAt: string
}

export type OrganisationData = Omit<
  Organisation,
  '_id' | 'isXeroConnected' | 'xeroNeedsReconnect' | 'plan'
>

type ApiContext = {
  login: (data: AuthFormData) => Promise<boolean>
  signup: (data: AuthFormData) => Promise<boolean>
  logout: () => void
  uploadImage: (file: File) => Promise<string>
  removeImageBackground: (imageUrl: string) => Promise<{
    sourceS3Key: string
    outputS3Key: string
    url: string
    imageId: string
  }>
  syncCompany: (company: Partial<Company>) => void
  syncSettings: (settings: Partial<Company['settings']>) => void
  reAuthenticate: () => Promise<boolean>
  convertDemoToUser: (data: AuthFormData) => Promise<boolean>
  downloadPDF: (
    company: Company,
    document: FFEDocument,
    suppliers: Supplier[],
    documentType: string
  ) => any
  loadDocument: (id: string) => Promise<FFEDocument>
  loadDocuments: () => Promise<FFEDocument[]>
  findDocuments: (
    options: FindQueryParams<FFEDocument>
  ) => Promise<PaginatedResponse<FFEDocument>>
  updateDocument: (document: FFEDocument) => Promise<FFEDocument>
  createDocument: (document: Omit<FFEDocument, '_id'>) => Promise<FFEDocument>
  removeDocument: (id: string) => Promise<void>
  createPurchaseOrder: (
    data: Omit<CompletedPurchaseOrder, '_id'>
  ) => Promise<CompletedPurchaseOrder>
  updatePurchaseOrder: (
    id: string,
    data: CompletedPurchaseOrder
  ) => Promise<CompletedPurchaseOrder>
  removePurchaseOrder: (id: string) => Promise<void>
  findPurchaseOrders: () => Promise<CompletedPurchaseOrder[]>
  getPurchaseOrder: (id: string) => Promise<CompletedPurchaseOrder>
  downloadPurchaseOrderPDF: (
    company: Company,
    purchaseOrder: CompletedPurchaseOrder
  ) => Promise<Blob>
  findSuppliers: (
    options: FindQueryParams<Supplier>
  ) => Promise<PaginatedResponse<Supplier>>
  getSupplier: (id: string) => Promise<Supplier>
  createSupplier: (supplierData: Omit<Supplier, '_id'>) => Promise<Supplier>
  updateSupplier: (
    id: string,
    supplierData: Partial<Supplier>
  ) => Promise<Supplier>
  removeSupplier: (id: string) => Promise<void>
  archiveSupplier: (id: string) => Promise<void>
  connectXero: (code: string, scopes: Array<string>) => Promise<XeroConnection>
  disconnectXero: () => Promise<void>
  getXeroConnection: () => Promise<PaginatedResponse<XeroConnection>>
  sendPurchaseOrderToXero: (purchaseOrderId: string) => Promise<{
    success: boolean
    data: {
      link: string
    }
  }>
  loadPurchaseOrders: (documentId: string) => Promise<CompletedPurchaseOrder[]>
  loadTeamMembers: () => Promise<User[]>
  patchTeamMember: (id: string, data: Partial<User>) => Promise<TeamMember>
  acceptSignupInvite: (
    password: string,
    inviteToken: string,
    token: string
  ) => Promise<boolean>
  inviteTeamMember: (
    user: Pick<User, 'firstName' | 'lastName' | 'email' | 'roles'>,
    token: string
  ) => Promise<User>
  uploadLineItemToProductLibrary: (
    lineItem: LineItem,
    supplierItemCode?: string
  ) => Promise<void>
  uploadMaterialToProductLibrary: (
    material: Material,
    supplierItemCode?: string
  ) => Promise<void>
  findProductLibraryItem: (
    options: FindQueryParams<ProductLibraryItem>
  ) => Promise<PaginatedResponse<ProductLibraryItem>>
  removeProductLibraryItem: (id: string) => Promise<void>
  addProductFromLibraryToSchedule: (
    productId: string,
    scheduleId: string,
    groupId?: string
  ) => Promise<void>
  createBillingSession: () => Promise<{ url: string }>
  createCheckoutSession: (billingPeriod: 'monthly' | 'yearly') => Promise<{ url: string }>
  getXeroAccounts: () => Promise<Array<{ code: string; name: string }>>
  setExpensesAccount: (accountCode: string) => Promise<void>
  loginAsUser: (email: string) => Promise<boolean>
  addItemByUrl: (url: string) => Promise<Pick<LineItem,'name' | 'description' | 'image' | 'supplier' | 'supplierItemCode' | 'brand' | 'productUrl' | 'dimensions' | 'additionalImages'>>
  getPresignedUpload: (data: { fileName: string; fileType: string; fileSize: number; folder?: string }) => Promise<{ presignedUrl: string; s3Key: string }>
  addItemsByQuote: (data: { s3Key: string }) => Promise<{ lineItems: any[] }>
  duplicateDocument: (documentId: string) => Promise<{ _id: string }>
}

const ApiContext = createContext<ApiContext | null>(null)
const ApiContextProvider = ApiContext.Provider

export const convertFullCompanyToApi = (company: Company): Organisation => {
  return convertCompanyToApi(company) as Organisation
}

export const convertCompanyToApi = (
  partialCompany: Partial<Company>
): Partial<Organisation> => ({
  address: partialCompany.address,
  country: partialCompany.country,
  email: partialCompany.email,
  logo: partialCompany.logo,
  name: partialCompany.name,
  phone: partialCompany.phone,
  isXeroConnected: partialCompany.isXeroConnected,
  xeroNeedsReconnect: partialCompany.xeroNeedsReconnect,
  plan: partialCompany.plan,
  primaryColor: partialCompany.settings?.primaryColor,
  defaultMargin: partialCompany.settings?.defaultMargin,
  taxRate: partialCompany.settings?.taxRate,
  taxNumber: partialCompany.settings?.taxNumber,
  purchaseOrderSettings: partialCompany.purchaseOrderSettings,
  projectSettings: partialCompany.projectSettings,
  _id: partialCompany._id!
})

const convertOrganisationToCompany = (organisation: Organisation): Company => ({
  _id: organisation._id,
  address: organisation.address,
  email: organisation.email,
  name: organisation.name,
  phone: organisation.phone,
  logo: organisation.logo,
  plan: organisation.plan,
  country: organisation.country,
  isXeroConnected: organisation.isXeroConnected,
  xeroNeedsReconnect: organisation.xeroNeedsReconnect,
  settings: {
    taxNumber: organisation.taxNumber,
    primaryColor: organisation.primaryColor,
    defaultMargin: organisation.defaultMargin,
    taxRate: organisation.taxRate,
  },
  purchaseOrderSettings: organisation.purchaseOrderSettings,
  projectSettings: organisation.projectSettings,
  createdAt: organisation.createdAt
})

export const ApiProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const {
    user,
    company,
    updateUser,
    setCompany,
    updateSettings,
    patchCompany,
    setTeamMembers
  } = useCompanyStore()
  const tax = useTaxSystem()

  const login = async (data: AuthFormData) => {
    const response = await authApi.login(data)
    const companyResponse = await apiClient.get<
      PaginatedResponse<Organisation>
    >('/organisations?$limit=1')

    const organisation = companyResponse.data[0]

    updateUser(response.user)
    setCompany(convertOrganisationToCompany(organisation))
    setTeamMembers(await loadTeamMembers())

    return true
  }

  const loginAsUser = async (email: string) => {
    const response = await authApi.loginAsUser(email)
    const companyResponse = await apiClient.get<
      PaginatedResponse<Organisation>
    >('/organisations?$limit=1')

    const organisation = companyResponse.data[0]

    updateUser(response.user)
    setCompany(convertOrganisationToCompany(organisation))
    setTeamMembers(await loadTeamMembers())

    return true
  }

  const signup = async (data: AuthFormData) => {
    const response = await authApi.signup(
      data,
      convertFullCompanyToApi({
        ...company,
        settings: {
          ...company.settings,
          taxRate: company.settings.taxRate
        }
      })
    )

    updateUser(response.user)
    const organisation = response.company
    setCompany(convertOrganisationToCompany(organisation))
    setTeamMembers(await loadTeamMembers())

    return true
  }

  const convertDemoToUser = async (data: AuthFormData) => {
    const response = await authApi.convertDemoToUser(data, user!)
    updateUser(response.user)
    const organisation = response.company
    setCompany(convertOrganisationToCompany(organisation))

    return true
  }

  const reAuthenticate = async () => {
    if (!localStorage.getItem('auth_token')) {
      // redirect to login
      window.location.href = '/login'
      return false
    }
    const authResponse = await authApi.reAuthenticate()
    const companyResponse = await apiClient.get<
      PaginatedResponse<Organisation>
    >('/organisations?$limit=1')

    const organisation = companyResponse.data[0]

    updateUser(authResponse.user)
    setCompany(convertOrganisationToCompany(organisation))
    setTeamMembers(await loadTeamMembers())

    return true
  }

  const logout = () => {
    authApi.logout()
    localStorage.clear()
    updateUser(null)
    setCompany(initialCompany)
    setTeamMembers([])
    updateSettings({
      ...initialCompany.settings,
      defaultMargin: 30,
      taxRate: tax.defaultRate
    })
  }

  const toBase64 = (file: File) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => resolve(reader.result as string)
      reader.onerror = reject
    })

  const uploadImage = async (file: File) => {
    const compressedFile = await imageCompression(file, {
      maxSizeMB: 0.5,
      maxWidthOrHeight: 1920,
      useWebWorker: true
    })

    const base64 = await toBase64(compressedFile)

    const response = await apiClient.post<{ url: string }>('/images', {
      blob: base64.split(',').pop(),
      name: file.name,
      type: file.type
      // token: !user ? captcha : undefined
    })
    // setRefreshReCaptcha((r: boolean) => !r)
    return response.url
  }

  const removeImageBackground = async (imageUrl: string) => {
    const response = await apiClient.post<{
      sourceS3Key: string
      outputS3Key: string
      url: string
      imageId: string
    }>('/image-background-removal', { imageUrl })
    return response
  }

  const updateCompanyToApi = async (
    partialCompany: Partial<Company>,
    currentCompany: Company
  ) => {
    if (!currentCompany._id) return

    await apiClient.patch<Organisation>(
      `/organisations/${currentCompany._id}`,
      convertCompanyToApi(partialCompany)
    )
  }

  const debouncedUpdateCompany = useRef(debounce(updateCompanyToApi, 1500))

  const syncCompany = (partialCompany: Partial<Company>) => {
    patchCompany(partialCompany)
    localStorage.setItem(
      'company',
      JSON.stringify({ ...company, ...partialCompany })
    )
    debouncedUpdateCompany.current(partialCompany, company)
  }

  const syncSettings = (settings: Partial<Company['settings']>) => {
    syncCompany({ settings: { ...company.settings, ...settings } })
  }

  const connectXero = async (code: string, scopes: Array<string>) => {
    const response = await apiClient.post<XeroConnection>('/xero-connections', {
      code,
      scopes
    })
    return response
  }

  const disconnectXero = async () => {
    const response = await apiClient.get<PaginatedResponse<XeroConnection>>(
      '/xero-connections?$limit=1'
    )
    if (response.total === 0) {
      throw new Error('No Xero connection found')
    }
    await apiClient.delete<XeroConnection>(
      `/xero-connections/${response.data[0]._id}`
    )
  }

  const getXeroConnection = async () => {
    const response = await apiClient.get<PaginatedResponse<XeroConnection>>(
      '/xero-connections?$limit=1'
    )
    return response
  }

  const sendPurchaseOrderToXero = async (purchaseOrderId: string) => {
    const response = await apiClient.post(`/xero-actions`, {
      data: {
        action: 'syncPurchaseOrder',
        purchaseOrderId
      }
    })
    return response as {
      success: boolean
      data: {
        link: string
      }
    }
  }

  const downloadPDF = async (
    company: Company,
    document: FFEDocument,
    suppliers: Supplier[],
    documentType: string
  ) => {
    const newDoc = { ...document }
    // replace all supplier fields on line items and materials with supplier names
    newDoc.lineItems = newDoc.lineItems.map((li) => {
      const supplier = suppliers.find((s) => s._id === li.supplier)
      return {
        ...li,
        supplier: supplier?.name
      }
    })
    newDoc.materials = newDoc.materials.map((m) => {
      const supplier = suppliers.find((s) => s._id === m.supplier)
      return {
        ...m,
        supplier: supplier?.name
      }
    })

    const response = await apiClient.raw('/print', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/pdf'
      },
      body: JSON.stringify({
        embeddedOrganisation: convertCompanyToApi(company),
        embeddedDocument: newDoc,
        documentType
      })
    })
    return await response.blob()
  }

  const downloadPurchaseOrderPDF = async (
    company: Company,
    document: CompletedPurchaseOrder
  ) => {
    const purchaseOrder = { ...document }
    if (purchaseOrder.documentId && !user) {
      delete purchaseOrder.documentId
    }
    const response = await apiClient.raw('/print', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/pdf'
      },
      body: JSON.stringify({
        embeddedOrganisation: convertCompanyToApi(company),
        embeddedPurchaseOrder: purchaseOrder,
        documentType: 'purchaseOrder'
      })
    })
    return await response.blob()
  }

  const loadDocument = async (id: string) => {
    const response = await apiClient.get<FFEDocument>(`/documents/${id}`)
    return response
  }

  const findDocuments = async (options: FindQueryParams<FFEDocument>) => {
    const response = await apiClient.get<PaginatedResponse<FFEDocument>>(
      `/documents?${constructFeathersQueryParams(options)}`
    )
    return response
  }

  const loadDocuments = async () => {
    const response =
      await apiClient.get<PaginatedResponse<FFEDocument>>('/documents')
    return response.data
  }

  const updateDocument = async (document: FFEDocument) => {
    const response = await apiClient.patch<FFEDocument>(
      `/documents/${document._id}`,
      document
    )
    return response
  }

  const createDocument = async (document: Omit<FFEDocument, '_id'>) => {
    const response = await apiClient.post<FFEDocument>('/documents', document)
    return response
  }

  const removeDocument = async (id: string) => {
    await apiClient.delete(`/documents/${id}`)
  }

  const loadTeamMembers = async () => {
    const response =
      await apiClient.get<PaginatedResponse<User>>('/users?$limit=1000')
    return response.data
  }

  const acceptSignupInvite = async (
    password: string,
    inviteToken: string,
    token: string
  ) => {
    const response = await apiClient.post<
      { email: string },
      { token: string; inviteToken: string; password: string }
    >(`/accept-signup-invite`, {
      token: token,
      inviteToken: inviteToken,
      password: password
    })
    await login({ email: response.email, password })
    return true
  }

  const inviteTeamMember = async (
    user: Pick<User, 'firstName' | 'lastName' | 'email' | 'roles'>,
    token: string
  ) => {
    const newUser = await apiClient.post<User>('/users', {
      ...user,
      token,
      password: 'pass',
      organisationId: company._id
    })
    return newUser
  }

  const createPurchaseOrder = async (
    data: Omit<CompletedPurchaseOrder, '_id'>
  ) => {
    const response = await apiClient.post<CompletedPurchaseOrder>(
      '/purchase-orders',
      data
    )
    return response
  }

  const updatePurchaseOrder = async (
    id: string,
    data: CompletedPurchaseOrder
  ) => {
    const response = await apiClient.patch<CompletedPurchaseOrder>(
      `/purchase-orders/${id}`,
      data
    )
    return response
  }

  const removePurchaseOrder = async (id: string) => {
    await apiClient.delete(`/purchase-orders/${id}`)
  }

  const findPurchaseOrders = async () => {
    const response =
      await apiClient.get<PaginatedResponse<CompletedPurchaseOrder>>(
        '/purchase-orders'
      )
    return response.data
  }

  const getPurchaseOrder = async (id: string) => {
    const response = await apiClient.get<CompletedPurchaseOrder>(
      `/purchase-orders/${id}`
    )
    return response
  }

  const getSupplier = async (id: string) => {
    return await apiClient.get<Supplier>(`/suppliers/${id}`)
  }

  const createSupplier = async (supplierData: Omit<Supplier, '_id'>) => {
    const response = await apiClient.post<Supplier>('/suppliers', supplierData)
    return response
  }

  const updateSupplier = async (
    id: string,
    supplierData: Partial<Supplier>
  ) => {
    delete supplierData._id
    const response = await apiClient.patch<Supplier>(
      `/suppliers/${id}`,
      supplierData
    )
    return response
  }

  const removeSupplier = async (id: string) => {
    await apiClient.delete(`/suppliers/${id}`)
  }

  const archiveSupplier = async (id: string) => {
    await apiClient.patch(`/suppliers/${id}`, { isArchived: true })
  }

  const constructFeathersQueryParams = <T,>(options: FindQueryParams<T>) => {
    // transform into url query params
    const query = new URLSearchParams()
    if (options.$limit) query.append('$limit', options.$limit.toString())
    if (options.$skip) query.append('$skip', options.$skip.toString())
    if (options.$search) query.append('$search', options.$search)
    if (options.$sort) {
      for (const key in options.$sort) {
        query.append(`$sort[${key}]`, `${options.$sort[key]}`)
      }
    }
    if (options.$select) {
      for (const key of options.$select) {
        query.append('$select[]', key.toString())
      }
    }

    if (options.query) {
      for (const key in options.query) {
        query.append(key, options.query[key])
      }
    }
    // replace all %24 with $
    return query
      .toString()
      .replace(/%24/g, '$')
      .replace(/%5B/g, '[')
      .replace(/%5D/g, ']')
  }

  const findSuppliers = async (options: FindQueryParams<Supplier>) => {
    const response = await apiClient.get<PaginatedResponse<Supplier>>(
      `/suppliers?${constructFeathersQueryParams(options)}`
    )
    return response
  }

  const loadPurchaseOrders = async (
    documentId: string
  ): Promise<CompletedPurchaseOrder[]> => {
    const response = await apiClient.get<
      PaginatedResponse<CompletedPurchaseOrder>
    >(`/purchase-orders?$limit=500&documentId=${documentId}`)
    return response.data
  }

  const patchTeamMember = async (id: string, data: Partial<User>) => {
    return await apiClient.patch<User>(`/users/${id}`, data)
  }

  const findProductLibraryItem = async (
    options: FindQueryParams<ProductLibraryItem>
  ) => {
    const response = await apiClient.get<PaginatedResponse<ProductLibraryItem>>(
      `/product-library-item?${constructFeathersQueryParams(options)}`
    )
    return response
  }

  const uploadLineItemToProductLibrary = async (
    lineItem: LineItem,
    supplierName?: string
  ) => {
    await apiClient.post('/product-library-item', {
      name: lineItem.name,
      supplier: lineItem.supplier,
      supplierName: supplierName,
      supplierItemCode: lineItem.supplierItemCode,
      type: 'lineItem',
      lineItem: {
        image: lineItem.image,
        description: lineItem.description,
        internalNotes: lineItem.internalNotes,
        brand: lineItem.brand,
        productUrl: lineItem.productUrl,
        dimensions: lineItem.dimensions,
        additionalImages: lineItem.additionalImages
      }
    })
  }

  const uploadMaterialToProductLibrary = async (
    material: Material,
    supplierName?: string
  ) => {
    await apiClient.post('/product-library-item', {
      name: material.name,
      supplier: material.supplier,
      supplierName: supplierName,
      supplierItemCode: material.supplierItemCode,
      type: 'material',
      material: {
        image: material.image,
        materialType: material.materialType,
        unit: material.unit,
        type: material.type
      }
    })
  }

  const removeProductLibraryItem = async (id: string) => {
    await apiClient.delete(`/product-library-item/${id}`)
  }

  const addProductFromLibraryToSchedule = async (
    productId: string,
    scheduleId: string,
    groupId?: string
  ) => {
    // Increment timesUsed on product library item
    const productLibraryItem = await apiClient.get<ProductLibraryItem>(
      `/product-library-item/${productId}`
    )
    await apiClient.patch(`/product-library-item/${productId}`, {
      timesUsed: productLibraryItem.timesUsed + 1
    })

    // Patch document line items
    if (productLibraryItem.type === 'lineItem') {
      const schedule = await apiClient.get<FFEDocument>(
        `/documents/${scheduleId}`
      )
      const existingCodes = schedule.lineItems.map((item) => item.code)
      const lineItem = productLibraryItem.lineItem!
      const newLineItem: LineItem = {
        id: crypto.randomUUID(),
        code: generateNextCode(existingCodes),
        name: productLibraryItem.name,
        status: 'specified',
        description: lineItem.description,
        image: lineItem.image,
        quantity: 1,
        costPrice: 0,
        margin: 0,
        sellPrice: 0,
        supplier: productLibraryItem.supplier,
        supplierItemCode: productLibraryItem.supplierItemCode,
        brand: lineItem.brand,
        productUrl: lineItem.productUrl,
        internalNotes: lineItem.internalNotes,
        leadTime: '',
        materials: [],
        dimensions: lineItem.dimensions,
        groupId: groupId
      }
      const updatedDocument = {
        ...schedule,
        lineItems: [...schedule.lineItems, newLineItem]
      }
      await apiClient.patch(`/documents/${scheduleId}`, updatedDocument)
    }

    // Patch document materials
    if (productLibraryItem.type === 'material') {
      const schedule = await apiClient.get<FFEDocument>(
        `/documents/${scheduleId}`
      )
      const material = productLibraryItem.material!
      const newMaterial: Material = {
        id: crypto.randomUUID(),
        name: productLibraryItem.name,
        image: material.image,
        materialType: material.materialType,
        unit: material.unit,
        type: material.type,
        costPerUnit: 0,
        sellPricePerUnit: 0,
        margin: 0
      }
      const updatedDocument = {
        ...schedule,
        materials: [...schedule.materials, newMaterial]
      }
      await apiClient.patch(`/documents/${scheduleId}`, updatedDocument)
    }
  }

  const createCheckoutSession = async (billingPeriod: 'monthly' | 'yearly') => {
    const response = await apiClient.post<{
      url: string
    }>('/checkout-session', { billingPeriod })
    return response
  }

  const createBillingSession = async () => {
    const response = await apiClient.post<{
      url: string
    }>('/billing-portal-session', {})
    return response
  }

  const getXeroAccounts = async () => {
    const response = await apiClient.post<
      { data: Array<{ code: string; name: string }> },
      { data: { action: string } }
    >('/xero-actions', {
      data: {
        action: 'getAccountCodes'
      }
    })
    return response.data
  }

  const setExpensesAccount = async (accountCode: string) => {
    await apiClient.post('/xero-actions', {
      data: {
        action: 'setExpensesAccount',
        accountCode
      }
    })
  }

  const addItemByUrl = async (url: string) => {
    const response = await apiClient.post<Pick<LineItem,'name' | 'description' | 'image' | 'supplier' | 'supplierItemCode' | 'brand' | 'productUrl' | 'dimensions' | 'additionalImages'>>('/add-item-by-url', { url })
    return response
  }

  const getPresignedUpload = async (data: { fileName: string; fileType: string; fileSize: number; folder?: string }) => {
    const response = await apiClient.post<{ presignedUrl: string; s3Key: string }>('/presigned-upload', data)
    return response
  }

  const addItemsByQuote = async (data: { s3Key: string }) => {
    const response = await apiClient.post<{ lineItems: any[] }>('/add-items-by-quote', data)
    return response
  }

  const duplicateDocument = async (documentId: string) => {
    const response = await apiClient.post<{ _id: string }>('/duplicate-document', { documentId })
    return response
  }

  const api = {
    // Auth methods
    convertDemoToUser,
    login,
    signup,
    logout,
    uploadImage,
    removeImageBackground,
    syncCompany,
    syncSettings,
    reAuthenticate,
    downloadPDF,
    loadDocument,
    findDocuments,
    loadDocuments,
    updateDocument,
    createDocument,
    removeDocument,
    createPurchaseOrder,
    updatePurchaseOrder,
    removePurchaseOrder,
    findPurchaseOrders,
    getPurchaseOrder,
    downloadPurchaseOrderPDF,
    findSuppliers,
    getSupplier,
    createSupplier,
    updateSupplier,
    removeSupplier,
    archiveSupplier,
    getXeroConnection,
    connectXero,
    disconnectXero,
    sendPurchaseOrderToXero,
    loadPurchaseOrders,
    loadTeamMembers,
    patchTeamMember,
    acceptSignupInvite,
    inviteTeamMember,
    uploadLineItemToProductLibrary,
    uploadMaterialToProductLibrary,
    findProductLibraryItem,
    removeProductLibraryItem,
    addProductFromLibraryToSchedule,
    createBillingSession,
    createCheckoutSession,
    getXeroAccounts,
    setExpensesAccount,
    loginAsUser,
    addItemByUrl,
    getPresignedUpload,
    addItemsByQuote,
    duplicateDocument
  }

  return (
    <>
      <ApiContextProvider value={api}>{children}</ApiContextProvider>
    </>
  )
}

export const useApi = () => {
  const context = useContext(ApiContext)
  if (!context) {
    throw new Error('useApi must be used within an ApiProvider')
  }
  return context
}
